import { getExperienceTypeLabel } from '@/utils/experienceLabels';

export interface CategoryBadgeColors {
  badgeBackground: string;
  badgeBorder?: string;
  badgeShadow?: string;
  iconBackground?: string;
  iconBorder?: string;
  textColor?: string;
}

export interface CategoryMeta {
  label: string;
  icon: string;
  colors: CategoryBadgeColors;
}

const DEFAULT_BADGE_COLORS: CategoryBadgeColors = {
  badgeBackground: 'rgba(24, 24, 27, 0.65)',
  badgeBorder: 'rgba(244, 244, 245, 0.28)',
  badgeShadow: '0px 4px 12px rgba(8, 8, 10, 0.35)',
  iconBackground: 'rgba(255, 255, 255, 0.16)',
  iconBorder: 'rgba(255, 255, 255, 0.24)',
  textColor: 'rgba(244, 244, 245, 0.96)',
};

const DEFAULT_CATEGORY_META: CategoryMeta = {
  label: 'Experience',
  icon: '/assets/images/icons/Group.svg',
  colors: DEFAULT_BADGE_COLORS,
};

const CATEGORY_META_MAP: Record<string, CategoryMeta> = {
  activity: {
    label: getExperienceTypeLabel('activities'),
    icon: '/assets/images/icons/guided.svg',
    colors: {
      badgeBackground: 'rgba(255, 255, 255, 0.10)',
      badgeBorder: undefined,
      badgeShadow: '4px 4px 4px 0 rgba(0, 0, 0, 0.10)',
      iconBackground: 'rgba(241, 241, 239, 0.19)',
      iconBorder: undefined,
      textColor: '#FFFFFF',
    },
  },
  activities: {
    label: getExperienceTypeLabel('activities'),
    icon: '/assets/images/icons/guided.svg',
    colors: {
      badgeBackground: 'rgba(255, 255, 255, 0.10)',
      badgeBorder: undefined,
      badgeShadow: '4px 4px 4px 0 rgba(0, 0, 0, 0.10)',
      iconBackground: 'rgba(241, 241, 239, 0.19)',
      iconBorder: undefined,
      textColor: '#FFFFFF',
    },
  },
  guided: {
    label: 'Guided',
    icon: '/assets/images/icons/guided.svg',
    colors: {
      badgeBackground: 'rgba(255, 255, 255, 0.10)',
      badgeBorder: undefined,
      badgeShadow: '4px 4px 4px 0 rgba(0, 0, 0, 0.10)',
      iconBackground: 'rgba(241, 241, 239, 0.19)',
      iconBorder: undefined,
      textColor: '#FFFFFF',
    },
  },
  package: {
    label: getExperienceTypeLabel('package'),
    icon: '/assets/images/icons/packages.svg',
    colors: {
      badgeBackground: 'rgba(148, 101, 48, 0.88)',
      badgeBorder: 'rgba(255, 235, 201, 0.35)',
      badgeShadow: '0px 6px 18px rgba(93, 64, 31, 0.45)',
      iconBackground: 'rgba(254, 238, 209, 0.24)',
      iconBorder: 'rgba(254, 238, 209, 0.38)',
      textColor: 'rgba(255, 249, 238, 0.96)',
    },
  },
  packages: {
    label: getExperienceTypeLabel('package'),
    icon: '/assets/images/icons/packages.svg',
    colors: {
      badgeBackground: 'rgba(148, 101, 48, 0.88)',
      badgeBorder: 'rgba(255, 235, 201, 0.35)',
      badgeShadow: '0px 6px 18px rgba(93, 64, 31, 0.45)',
      iconBackground: 'rgba(254, 238, 209, 0.24)',
      iconBorder: 'rgba(254, 238, 209, 0.38)',
      textColor: 'rgba(255, 249, 238, 0.96)',
    },
  },
  riders_corner: {
    label: getExperienceTypeLabel('riders-corner'),
    icon: '/assets/images/icons/motorcycle.svg',
    colors: {
      badgeBackground: 'rgba(60, 61, 69, 0.82)',
      badgeBorder: 'rgba(224, 225, 231, 0.32)',
      badgeShadow: '0px 6px 18px rgba(27, 28, 32, 0.45)',
      iconBackground: 'rgba(235, 236, 243, 0.18)',
      iconBorder: 'rgba(235, 236, 243, 0.32)',
      textColor: 'rgba(237, 238, 245, 0.96)',
    },
  },
  riderscorner: {
    label: getExperienceTypeLabel('riders-corner'),
    icon: '/assets/images/icons/motorcycle.svg',
    colors: {
      badgeBackground: 'rgba(60, 61, 69, 0.82)',
      badgeBorder: 'rgba(224, 225, 231, 0.32)',
      badgeShadow: '0px 6px 18px rgba(27, 28, 32, 0.45)',
      iconBackground: 'rgba(235, 236, 243, 0.18)',
      iconBorder: 'rgba(235, 236, 243, 0.32)',
      textColor: 'rgba(237, 238, 245, 0.96)',
    },
  },
  riders: {
    label: getExperienceTypeLabel('riders-corner'),
    icon: '/assets/images/icons/motorcycle.svg',
    colors: {
      badgeBackground: 'rgba(60, 61, 69, 0.82)',
      badgeBorder: 'rgba(224, 225, 231, 0.32)',
      badgeShadow: '0px 6px 18px rgba(27, 28, 32, 0.45)',
      iconBackground: 'rgba(235, 236, 243, 0.18)',
      iconBorder: 'rgba(235, 236, 243, 0.32)',
      textColor: 'rgba(237, 238, 245, 0.96)',
    },
  },
  signature: {
    label: getExperienceTypeLabel('signature'),
    icon: '/assets/images/icons/signature.svg',
    colors: {
      badgeBackground: 'rgba(124, 86, 46, 0.9)',
      badgeBorder: 'rgba(243, 221, 200, 0.30)',
      badgeShadow: '0px 6px 18px rgba(78, 52, 29, 0.45)',
      iconBackground: 'rgba(249, 231, 214, 0.22)',
      iconBorder: 'rgba(249, 231, 214, 0.36)',
      textColor: 'rgba(254, 247, 241, 0.96)',
    },
  },
  signature_experience: {
    label: getExperienceTypeLabel('signature'),
    icon: '/assets/images/icons/signature.svg',
    colors: {
      badgeBackground: 'rgba(124, 86, 46, 0.9)',
      badgeBorder: 'rgba(243, 221, 200, 0.30)',
      badgeShadow: '0px 6px 18px rgba(78, 52, 29, 0.45)',
      iconBackground: 'rgba(249, 231, 214, 0.22)',
      iconBorder: 'rgba(249, 231, 214, 0.36)',
      textColor: 'rgba(254, 247, 241, 0.96)',
    },
  },
  group: {
    label: 'Group',
    icon: '/assets/images/icons/Group.svg',
    colors: {
      badgeBackground: 'rgba(88, 78, 105, 0.85)',
      badgeBorder: 'rgba(224, 210, 245, 0.35)',
      badgeShadow: '0px 6px 18px rgba(48, 42, 59, 0.45)',
      iconBackground: 'rgba(238, 226, 255, 0.22)',
      iconBorder: 'rgba(238, 226, 255, 0.34)',
      textColor: 'rgba(244, 237, 255, 0.96)',
    },
  },
  stay_experience: {
    label: getExperienceTypeLabel('stays'),
    icon: '/assets/images/icons/home.svg',
    colors: {
      badgeBackground: 'rgba(91, 78, 105, 0.85)',
      badgeBorder: 'rgba(220, 210, 235, 0.35)',
      badgeShadow: '0px 6px 18px rgba(55, 48, 65, 0.45)',
      iconBackground: 'rgba(240, 235, 250, 0.22)',
      iconBorder: 'rgba(240, 235, 250, 0.34)',
      textColor: 'rgba(250, 245, 255, 0.96)',
    },
  },
  stayexperience: {
    label: getExperienceTypeLabel('stays'),
    icon: '/assets/images/icons/home.svg',
    colors: {
      badgeBackground: 'rgba(91, 78, 105, 0.85)',
      badgeBorder: 'rgba(220, 210, 235, 0.35)',
      badgeShadow: '0px 6px 18px rgba(55, 48, 65, 0.45)',
      iconBackground: 'rgba(240, 235, 250, 0.22)',
      iconBorder: 'rgba(240, 235, 250, 0.34)',
      textColor: 'rgba(250, 245, 255, 0.96)',
    },
  },
  default: DEFAULT_CATEGORY_META,
};

function normalizeKey(value?: string | null): string {
  if (!value) return '';
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function getCategoryMeta(
  type?: string | null,
  fallbackTitle?: string | null
): CategoryMeta {
  const normalizedType = normalizeKey(type);
  if (normalizedType && CATEGORY_META_MAP[normalizedType]) {
    return CATEGORY_META_MAP[normalizedType];
  }

  const normalizedFallback = normalizeKey(fallbackTitle);
  if (normalizedFallback && CATEGORY_META_MAP[normalizedFallback]) {
    const meta = CATEGORY_META_MAP[normalizedFallback];
    if (fallbackTitle && meta.label !== fallbackTitle) {
      return { ...meta, label: fallbackTitle };
    }
    return meta;
  }

  if (fallbackTitle) {
    return {
      label: fallbackTitle,
      icon: DEFAULT_CATEGORY_META.icon,
      colors: DEFAULT_BADGE_COLORS,
    };
  }

  return DEFAULT_CATEGORY_META;
}
